import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { getMovieById, getRecommendedMovies } from '@/mocks/movies';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(id || '');
  const relatedMovies = getRecommendedMovies().filter((m) => m.id !== id).slice(0, 6);

  if (!movie) {
    return (
      <>
        <Navbar />
        <main className="pt-16 bg-background-950 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-background-800 rounded-full">
              <i className="ri-error-warning-line text-foreground-500 text-3xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-100 mb-2">内容未找到</h1>
            <p className="text-foreground-500 mb-4">您要查看的影视内容不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-16 bg-background-950 min-h-screen">
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: movie.categories[0] === 'action' ? '动作' : movie.categories[0] === 'comedy' ? '喜剧' : movie.categories[0] === 'romance' ? '爱情' : movie.categories[0] === 'scifi' ? '科幻' : movie.categories[0] === 'horror' ? '恐怖' : movie.categories[0] === 'animation' ? '动画' : movie.categories[0] === 'drama' ? '剧情' : movie.categories[0] === 'suspense' ? '悬疑' : '全部', href: `/category/${movie.categories[0]}` },
            { label: movie.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-6">
          {/* Backdrop */}
          <div className="relative w-full h-[280px] md:h-[450px] rounded-xl overflow-hidden mb-8">
            <img
              src={movie.backdropUrl}
              alt={movie.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/40 to-transparent" />
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Poster Sidebar */}
            <aside className="lg:w-[300px] shrink-0 -mt-32 lg:-mt-40 relative z-10">
              <div className="sticky top-24">
                <div className="w-[200px] md:w-[280px] mx-auto lg:mx-0">
                  <div className="rounded-lg overflow-hidden" style={{ height: 'auto' }}>
                    <img
                      src={movie.posterUrl}
                      alt={`${movie.title} 海报`}
                      title={`${movie.title} - 七七影院高清在线观看`}
                      className="w-full object-cover object-top rounded-lg"
                    />
                  </div>
                  <div className="mt-4 flex flex-col gap-2">
                    <button className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-play-circle-line text-lg"></i>
                      立即播放
                    </button>
                    <button className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-background-800 hover:bg-background-700 text-foreground-300 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-heart-line"></i>
                      收藏
                    </button>
                  </div>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1">
              <header className="mb-6">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  {movie.type === 'tv' ? (
                    <span className="px-2 py-0.5 bg-accent-500/20 text-accent-400 text-xs rounded-md font-medium">电视剧</span>
                  ) : (
                    <span className="px-2 py-0.5 bg-primary-500/20 text-primary-400 text-xs rounded-md font-medium">电影</span>
                  )}
                  {movie.categories.map((cat) => {
                    const catName = cat === 'action' ? '动作' : cat === 'comedy' ? '喜剧' : cat === 'romance' ? '爱情' : cat === 'scifi' ? '科幻' : cat === 'horror' ? '恐怖' : cat === 'animation' ? '动画' : cat === 'drama' ? '剧情' : cat === 'suspense' ? '悬疑' : cat;
                    return (
                      <Link
                        key={cat}
                        to={`/category/${cat}`}
                        className="px-2 py-0.5 bg-background-800 text-foreground-500 text-xs rounded-md hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {catName}
                      </Link>
                    );
                  })}
                </div>

                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-100 mb-2">
                  {movie.title}
                </h1>
                <p className="text-sm text-foreground-500">{movie.titleEn}</p>

                <div className="flex items-center gap-4 mt-4 flex-wrap">
                  <div className="flex items-center gap-1">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500"></i>
                    </div>
                    <span className="text-lg font-semibold text-accent-400">{movie.rating}</span>
                    <span className="text-xs text-foreground-600">/ 10</span>
                  </div>
                  <span className="text-sm text-foreground-500">{movie.year}</span>
                  <span className="text-sm text-foreground-500">{movie.duration}</span>
                  <span className="text-sm text-foreground-500">{movie.region}</span>
                </div>
              </header>

              {/* Info Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mb-8 p-5 bg-background-900/50 rounded-lg">
                {[
                  { label: '导演', value: movie.director },
                  { label: '主演', value: movie.actors.join(' / ') },
                  { label: '类型', value: movie.categories.map((c) => {
                    const catName = c === 'action' ? '动作' : c === 'comedy' ? '喜剧' : c === 'romance' ? '爱情' : c === 'scifi' ? '科幻' : c === 'horror' ? '恐怖' : c === 'animation' ? '动画' : c === 'drama' ? '剧情' : c === 'suspense' ? '悬疑' : c;
                    return catName;
                  }).join(' / ') },
                  { label: '地区', value: movie.region },
                  { label: '语言', value: movie.language },
                  { label: '上映日期', value: movie.releaseDate },
                  movie.type === 'tv' ? { label: '集数', value: movie.episodeInfo || '' } : { label: '片长', value: movie.duration },
                ].map((item, i) => (
                  <div key={i} className="flex gap-2">
                    <span className="text-sm text-foreground-600 shrink-0 whitespace-nowrap">{item.label}：</span>
                    <span className="text-sm text-foreground-300">{item.value}</span>
                  </div>
                ))}
              </div>

              {/* Description */}
              <section className="mb-10">
                <h2 className="text-lg font-heading font-semibold text-foreground-200 mb-3">剧情简介</h2>
                <p className="text-sm text-foreground-400 leading-relaxed">
                  {movie.longDescription}
                </p>
              </section>

              {/* Related */}
              <section>
                <h2 className="text-lg font-heading font-semibold text-foreground-200 mb-4">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4" data-product-shop>
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} size="sm" />
                  ))}
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Movie',
            name: movie.title,
            description: movie.description,
            image: movie.posterUrl,
            dateCreated: movie.releaseDate,
            director: { '@type': 'Person', name: movie.director },
            actor: movie.actors.map((a) => ({ '@type': 'Person', name: a })),
            countryOfOrigin: { '@type': 'Country', name: '中国' },
            inLanguage: movie.language,
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: typeof window !== 'undefined' ? window.location.origin + '/' : '' },
              { '@type': 'ListItem', position: 2, name: movie.categories[0] === 'action' ? '动作' : movie.categories[0] === 'comedy' ? '喜剧' : movie.categories[0] === 'romance' ? '爱情' : movie.categories[0] === 'scifi' ? '科幻' : movie.categories[0] === 'horror' ? '恐怖' : movie.categories[0] === 'animation' ? '动画' : movie.categories[0] === 'drama' ? '剧情' : movie.categories[0] === 'suspense' ? '悬疑' : '全部', item: typeof window !== 'undefined' ? `${window.location.origin}/category/${movie.categories[0]}` : '' },
              { '@type': 'ListItem', position: 3, name: movie.title },
            ],
          }),
        }}
      />
    </>
  );
}